import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { App, message, Switch } from 'antd';
import { useSelfServiceLanguage } from '../../context/SelfServiceLanguageContext';
import { findProductByBarcode, searchProducts } from '../../utils/productsSearch';
import { isMarkingCode, parseMarkingCode } from '../../utils/markingCodeParser';
import { deviceSalesService } from '../../services/posApi';
import SelfServiceFooter from '../../components/SelfServiceFooter';
import './SelfService.css';

const SelfServiceShopScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useSelfServiceLanguage();
    const [messageApi, contextHolder] = message.useMessage();
    const [cashRegisterId, setCashRegisterId] = useState(null);
    const [quickProducts, setQuickProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [quickProductsModalVisible, setQuickProductsModalVisible] = useState(false);
    const [packageProduct, setPackageProduct] = useState(null);

    const [cart, setCart] = useState([]);
    const [barcodeBuffer, setBarcodeBuffer] = useState('');
    const [lastKeystroke, setLastKeystroke] = useState(0);
    const [customer, setCustomer] = useState(null);
    const [tapCount, setTapCount] = useState(0);
    const [tapTimer, setTapTimer] = useState(null);

    // Modals
    const [ageVerificationModalVisible, setAgeVerificationModalVisible] = useState(false);
    const [markingModalVisible, setMarkingModalVisible] = useState(false);
    const [pendingMarkingProduct, setPendingMarkingProduct] = useState(null);
    const [ageVerified, setAgeVerified] = useState(false);
    const [usePoints, setUsePoints] = useState(false);
    const [inactivityModalVisible, setInactivityModalVisible] = useState(false);
    const [countdown, setCountdown] = useState(15);
    const [productNotFoundModalVisible, setProductNotFoundModalVisible] = useState(false);

    // Load cart and cash register info from localStorage
    useEffect(() => {
        const savedCart = localStorage.getItem('self_service_cart');
        if (savedCart) {
            try {
                setCart(JSON.parse(savedCart));
            } catch (e) {
                console.error('Error loading cart:', e);
            }
        }

        // Load cash register ID and quick products
        const registerInfo = localStorage.getItem('device_register_info');
        console.log('Register info from localStorage:', registerInfo);
        if (registerInfo) {
            try {
                const parsed = JSON.parse(registerInfo);
                console.log('Parsed register info:', parsed);
                if (parsed?.cash_register?.id) {
                    setCashRegisterId(parsed.cash_register.id);
                }
                if (parsed?.cash_register?.quick_products) {
                    console.log('Found quick_products, calling loadQuickProducts');
                    loadQuickProducts(parsed.cash_register.quick_products);
                } else {
                    console.log('No quick_products found in register info');
                }
            } catch (e) {
                console.error('Error loading register info:', e);
            }
        } else {
            console.log('No device_register_info in localStorage');
        }
    }, []);

    // Inactivity timer - 60 seconds total, warning at 45 seconds (15 seconds before reset)
    useEffect(() => {
        let inactivityTimer;
        let warningTimer;
        let countdownInterval;

        const resetTimers = () => {
            clearTimeout(inactivityTimer);
            clearTimeout(warningTimer);
            clearInterval(countdownInterval);
            setInactivityModalVisible(false);
            setCountdown(15);

            // Set warning timer for 45 seconds (15 seconds before reset)
            warningTimer = setTimeout(() => {
                setInactivityModalVisible(true);
                setCountdown(15);

                // Countdown timer
                let timeLeft = 15;
                countdownInterval = setInterval(() => {
                    timeLeft--;
                    setCountdown(timeLeft);
                    if (timeLeft <= 0) {
                        clearInterval(countdownInterval);
                    }
                }, 1000);
            }, 45000); // 45 seconds

            // Set full reset timer for 60 seconds
            inactivityTimer = setTimeout(() => {
                handleInactivityReset();
            }, 60000); // 60 seconds
        };

        const handleInactivityReset = () => {
            // Clear cart and return to waiting screen
            setCart([]);
            localStorage.removeItem('self_service_cart');
            setCustomer(null);
            setAgeVerified(false);
            setInactivityModalVisible(false);
            navigate('/device/self-service');
        };

        const handleUserActivity = () => {
            resetTimers();
        };

        // Track user activity
        window.addEventListener('mousedown', handleUserActivity);
        window.addEventListener('keydown', handleUserActivity);
        window.addEventListener('touchstart', handleUserActivity);
        window.addEventListener('scroll', handleUserActivity);

        // Start timers
        resetTimers();

        return () => {
            clearTimeout(inactivityTimer);
            clearTimeout(warningTimer);
            clearInterval(countdownInterval);
            window.removeEventListener('mousedown', handleUserActivity);
            window.removeEventListener('keydown', handleUserActivity);
            window.removeEventListener('touchstart', handleUserActivity);
            window.removeEventListener('scroll', handleUserActivity);
        };
    }, [navigate]);

    // Загрузка быстрых товаров из конфига (без API)
    const loadQuickProducts = (quickProductsConfig) => {
        try {
            console.log('Loading quick products, config:', quickProductsConfig);
            const loadedProducts = [];
            const loadedCategories = [];
            let foundPackageProduct = null;

            for (const group of quickProductsConfig) {
                if (group.products && group.products.length > 0) {
                    // Проверяем, является ли это категорией пакетов (с двойными фигурными скобками)
                    if (group.group_name === '{{Добавить_пакет}}') {
                        // Сохраняем первый товар как товар для кнопки "Добавить пакет"
                        foundPackageProduct = {
                            ...group.products[0],
                            category: group.group_name,
                            displayName: group.products[0].name,
                            _needsFullData: true
                        };
                        console.log('Found package product:', foundPackageProduct);
                        // НЕ добавляем эту категорию в список категорий
                        continue;
                    }

                    // Добавляем товары из конфига напрямую
                    loadedProducts.push(...group.products.map(p => ({
                        ...p,
                        category: group.group_name || 'Без категории',
                        displayName: p.name,
                        // Пометим что нужно загрузить полные данные при добавлении в корзину
                        _needsFullData: true
                    })));

                    if (group.group_name && !loadedCategories.includes(group.group_name)) {
                        loadedCategories.push(group.group_name);
                    }
                }
            }

            console.log('Loaded products:', loadedProducts);
            console.log('Loaded categories:', loadedCategories);
            console.log('Package product:', foundPackageProduct);

            setQuickProducts(loadedProducts);
            setCategories(loadedCategories);
            setPackageProduct(foundPackageProduct);

            if (loadedCategories.length > 0) {
                setSelectedCategory(loadedCategories[0]);
            }
        } catch (error) {
            console.error('Error loading quick products:', error);
        }
    };

    // Save cart to localStorage
    useEffect(() => {
        if (cart.length > 0) {
            localStorage.setItem('self_service_cart', JSON.stringify(cart));
        } else {
            localStorage.removeItem('self_service_cart');
        }
    }, [cart]);

    // Process barcode function - declared early to avoid initialization errors
    const processBarcode = useCallback(async (code) => {
        // Check if loyalty card
        if (code.length === 16 && code.startsWith('1160')) {
            try {
                const customerResponse = await deviceSalesService.getCostumerByCardNumber(code);
                if (customerResponse.status === 'error') {
                    messageApi.error('Карта не найдена');
                } else {
                    setCustomer({
                        id: customerResponse.data.id,
                        full_name: customerResponse.data.full_name,
                        balance: customerResponse.data.balance
                    });
                    messageApi.success(`Карта активирована`);
                }
            } catch (error) {
                console.error('Error getting customer:', error);
                messageApi.error('Ошибка при получении данных клиента');
            }
            return;
        }

        // Check if marking code
        if (isMarkingCode(code)) {
            const parsed = parseMarkingCode(code);
            if (parsed) {
                const product = await findProductByBarcode(parsed.gtin);
                if (product) {
                    if (product.is_marking === 1) {
                        // Will be handled by addToCart later
                        setPendingMarkingProduct(product);
                        setMarkingModalVisible(true);
                    } else {
                        messageApi.warning('Этот товар не требует маркировки');
                    }
                } else {
                    // Play error sound
                    const audio = new Audio('/sounds/error.m4a');
                    audio.play().catch(err => console.error('Error playing sound:', err));
                    setProductNotFoundModalVisible(true);
                }
            }
            return;
        }

        // Regular barcode
        const product = await findProductByBarcode(code);
        if (product) {
            // Проверяем, требуется ли маркировка
            if (product.is_marking === 1) {
                setPendingMarkingProduct(product);
                setMarkingModalVisible(true);
            } else {
                // Will be handled after addToCart is defined
                setCart(prevCart => {
                    const existingItem = prevCart.find(item => item.id === product.id && !item.mark_code);
                    if (existingItem) {
                        return prevCart.map(item =>
                            item.id === product.id && !item.mark_code
                                ? { ...item, quantity: item.quantity + 1 }
                                : item
                        );
                    } else {
                        return [...prevCart, { ...product, quantity: 1 }];
                    }
                });
            }
        } else {
            // Play error sound
            const audio = new Audio('/sounds/error.m4a');
            audio.play().catch(err => console.error('Error playing sound:', err));
            setProductNotFoundModalVisible(true);
        }
    }, [messageApi]);

    // Handle initial barcode/product from navigation
    useEffect(() => {
        if (location.state?.barcode) {
            processBarcode(location.state.barcode);
            // Clear the state after processing to prevent re-processing on back navigation
            navigate(location.pathname, { replace: true, state: null });
        } else if (location.state?.productId) {
            // TODO: Load product by ID and add to cart
            // Clear the state after processing
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [location.state, processBarcode]);

    // Barcode scanner
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Block scanning if any modal is open
            if (productNotFoundModalVisible || markingModalVisible || ageVerificationModalVisible) {
                e.preventDefault();
                return;
            }

            const currentTime = Date.now();
            const timeDiff = currentTime - lastKeystroke;

            if (timeDiff > 200) {
                setBarcodeBuffer('');
            }
            setLastKeystroke(currentTime);

            if (e.key === 'Enter') {
                if (barcodeBuffer.length >= 4) {
                    processBarcode(barcodeBuffer);
                }
                setBarcodeBuffer('');
                e.preventDefault();
            } else if (e.key.length === 1) {
                setBarcodeBuffer(prev => prev + e.key);
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [barcodeBuffer, lastKeystroke, productNotFoundModalVisible, markingModalVisible, ageVerificationModalVisible, processBarcode]);

    // Внутренняя функция добавления в корзину без проверок
    const addToCartInternal = useCallback((product, markCode = null) => {
        setCart(prevCart => {
            if (markCode) {
                // Проверяем, не использован ли уже этот код маркировки
                const isDuplicateMarkingCode = prevCart.some(item =>
                    item.mark_code &&
                    Array.isArray(item.mark_code) &&
                    item.mark_code.includes(markCode)
                );

                if (isDuplicateMarkingCode) {
                    // Если код уже использован - ничего не делаем и показываем ошибку
                    const audio = new Audio('/sounds/error.m4a');
                    audio.play().catch(err => console.error('Error playing sound:', err));
                    return prevCart;
                }

                // Ищем товар с таким же ID, у которого уже есть mark_code
                const existingMarkedItem = prevCart.find(item =>
                    item.id === product.id && item.mark_code && Array.isArray(item.mark_code)
                );

                if (existingMarkedItem) {
                    // Добавляем маркировку в существующий массив
                    return prevCart.map(item =>
                        item === existingMarkedItem
                            ? { ...item, mark_code: [...item.mark_code, markCode], quantity: item.quantity + 1 }
                            : item
                    );
                } else {
                    // Создаем новую позицию с маркировкой
                    return [...prevCart, { ...product, quantity: 1, mark_code: [markCode] }];
                }
            } else {
                // Обычное добавление без маркировки
                const existingItem = prevCart.find(item => item.id === product.id && !item.mark_code);

                if (existingItem) {
                    return prevCart.map(item =>
                        item.id === product.id && !item.mark_code
                            ? { ...item, quantity: item.quantity + 1 }
                            : item
                    );
                } else {
                    return [...prevCart, { ...product, quantity: 1 }];
                }
            }
        });
    }, []);

    // Добавление товара в корзину с проверкой возраста
    const addToCart = useCallback(async (product, markCode = null) => {
        let fullProduct = product;

        // Если товар из быстрых товаров - загружаем полные данные
        if (product._needsFullData) {
            try {
                const products = await searchProducts(product.id.toString(), 'id', 1);
                if (products.length > 0) {
                    fullProduct = products[0];
                } else {
                    messageApi.error('Товар не найден');
                    return;
                }
            } catch (error) {
                console.error('Error loading product:', error);
                messageApi.error('Ошибка при загрузке товара');
                return;
            }
        }

        // Просто добавляем товар в корзину, проверка возраста будет перед оплатой
        addToCartInternal(fullProduct, markCode);
    }, [addToCartInternal, messageApi]);

    const handleProceedToPayment = () => {
        if (cart.length === 0) {
            messageApi.warning('Корзина пуста');
            return;
        }

        // Check if any items require age verification
        const hasAdultItems = cart.some(item => item.is_adult === 1);
        if (hasAdultItems && !ageVerified) {
            setAgeVerificationModalVisible(true);
            return;
        }

        navigate('/device/self-service/payment', { state: { cart, customer, usePoints } });
    };

    const handleAgeVerified = () => {
        setAgeVerified(true);
        setAgeVerificationModalVisible(false);
        // Теперь можно перейти к оплате
        navigate('/device/self-service/payment', { state: { cart, customer, usePoints } });
    };

    const handleMarkingScanned = (markCode) => {
        if (pendingMarkingProduct && markCode) {
            addToCart(pendingMarkingProduct, markCode);
            setPendingMarkingProduct(null);
            setMarkingModalVisible(false);
        }
    };

    const getTotalAmount = () => {
        return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    // Validate bonus amount (copied from device logic)
    const validateBonusAmount = (bonusAmount, cartItems, totalAmount) => {
        if (bonusAmount <= 0) return true;
        if (!Number.isInteger(bonusAmount)) return false;

        const originalTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const targetTotal = originalTotal - bonusAmount;

        let accumulatedTotal = 0;
        cartItems.forEach(item => {
            const itemTotal = item.price * item.quantity;
            const proportion = itemTotal / originalTotal;
            const adjustedTotal = Math.round(targetTotal * proportion * 100) / 100;
            const adjustedUnitPrice = Math.round((adjustedTotal / item.quantity) * 100) / 100;
            const itemTotalAmount = Math.round((adjustedUnitPrice * item.quantity) * 100) / 100;
            accumulatedTotal += itemTotalAmount;
        });

        const diff = Math.round((targetTotal - accumulatedTotal) * 100) / 100;
        if (Math.abs(diff) >= 0.01) {
            const firstItem = cartItems[0];
            const correctedUnitPrice = Math.round((firstItem.price - (bonusAmount * firstItem.price * firstItem.quantity / originalTotal) + diff / firstItem.quantity) * 100) / 100;
            const correctedTotal = Math.round((correctedUnitPrice * firstItem.quantity) * 100) / 100;
            accumulatedTotal = accumulatedTotal - Math.round((firstItem.price * firstItem.quantity) * 100) / 100 + correctedTotal;
        }

        const finalTotal = Math.round(accumulatedTotal * 100) / 100;
        const nonBonusTotal = Math.round((totalAmount - bonusAmount) * 100) / 100;

        return finalTotal === nonBonusTotal;
    };

    // Find maximum valid bonus amount (copied from device logic)
    const findMaxValidBonusAmount = (maxAmount, cartItems, totalAmount) => {
        let validAmount = Math.floor(maxAmount);

        while (validAmount > 0) {
            if (validateBonusAmount(validAmount, cartItems, totalAmount)) {
                return validAmount;
            }
            validAmount -= 1;
        }

        return 0;
    };

    const getPointsToUse = () => {
        if (!usePoints || !customer) return 0;
        const total = getTotalAmount();
        // Maximum 99% of total, rounded down to integer
        const maxTheoretical = Math.floor(Math.min(customer.balance, total * 0.99));
        // Find maximum mathematically valid amount
        const maxPoints = findMaxValidBonusAmount(maxTheoretical, cart, total);
        console.log('Bonus calculation:', { total, balance: customer.balance, maxTheoretical, maxPoints, usePoints });
        return maxPoints;
    };

    const getFinalAmount = () => {
        const total = getTotalAmount();
        const pointsDiscount = getPointsToUse();
        return total - pointsDiscount;
    };

    const handleRemoveItem = (itemId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== itemId));
    };

    // 5-tap menu for admin access
    const handleHeaderTap = () => {
        setTapCount(prev => prev + 1);

        if (tapTimer) {
            clearTimeout(tapTimer);
        }

        const timer = setTimeout(() => {
            setTapCount(0);
        }, 2000);

        setTapTimer(timer);

        if (tapCount + 1 === 5) {
            // Show admin menu
            navigate('/device/self-service/admin');
            setTapCount(0);
        }
    };

    return (
        <>
            {contextHolder}
            <div className="self-service-container">
            {/* Content */}
            <div className="self-service-content" style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                padding: '65px',
                paddingBottom: '175px', // Space for footer
                position: 'relative',
                height: 'calc(100vh - 110px)',
                overflow: 'hidden'
            }}>
                {/* Header with title and cash register ID + date */}
                <div
                    onClick={handleHeaderTap}
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 24,
                        width: '100%',
                        cursor: 'pointer',
                        userSelect: 'none'
                    }}
                >
                    <h1 style={{
                        fontSize: 60,
                        fontWeight: 700,
                        margin: 0,
                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                        color: '#17181A'
                    }}>
                        {t('yourPurchases')}
                    </h1>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        fontSize: 28,
                        fontWeight: 500,
                        color: 'rgba(30, 30, 30, 0.3)',
                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif'
                    }}>
                        {cashRegisterId && (
                            <span>#{cashRegisterId}</span>
                        )}
                        <span>
                            {new Date().toLocaleString('ru-RU', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </span>
                    </div>
                </div>

                {/* Cart items list - scrollable */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    width: '100%',
                    marginBottom: '16px'
                }}>
                    <div>
                        {cart.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                                <p style={{ fontSize: 20 }}>{t('scanHere')}</p>
                            </div>
                        ) : (
                            cart.map((item, index) => (
                                <div
                                    key={`${item.id}-${index}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '30px 0',
                                        gap: '16px'
                                    }}
                                >
                                    {/* Product name - takes remaining space, aligned left */}
                                    <div style={{
                                        flex: 1,
                                        fontSize: 28,
                                        fontWeight: 500,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: '#1E1E1E',
                                        textAlign: 'left'
                                    }}>
                                        {item.name}
                                    </div>

                                    {/* 21+ icon - only if adult product, left of trash icon */}
                                    {item.is_adult === 1 && (
                                        <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="17.5" cy="17.5" r="17.5" fill="#1E1E1E"/>
                                            <path d="M10.8612 20.3567H14.8026V22.1509L7 22.1665V21.7141L10.7811 17.72C11.3152 17.1479 11.6944 16.6695 11.9187 16.2846C12.1537 15.8894 12.2712 15.5045 12.2712 15.1301C12.2712 14.6829 12.1377 14.3188 11.8706 14.038C11.6036 13.7571 11.2458 13.6167 10.7972 13.6167C10.3486 13.6167 9.98539 13.7623 9.70768 14.0536C9.44065 14.3448 9.30714 14.7245 9.30714 15.1925C9.30714 15.3485 9.33384 15.5982 9.38725 15.9414H7.20828C7.13352 15.6814 7.09613 15.3901 7.09613 15.0677C7.09613 14.1524 7.44327 13.3567 8.13755 12.6806C8.84251 12.0045 9.73972 11.6665 10.8292 11.6665C11.8866 11.6665 12.7625 11.9837 13.4568 12.6182C14.1511 13.2423 14.4982 14.0224 14.4982 14.9585C14.4982 15.6554 14.3059 16.3054 13.9214 16.9087C13.5155 17.5328 12.9494 18.2141 12.2231 18.9525L10.8612 20.3567Z" fill="white"/>
                                            <path d="M19.8813 11.8537H20.6022V22.1509H18.4393V14.35L16.4366 14.9741L15.9239 13.1175L19.8813 11.8537Z" fill="white"/>
                                            <path d="M25.5428 20.8092V18.0476H22.7871V16.2534H25.5428V13.5075H27.6096V16.2534H30.3333V18.0476H27.6096V20.8092H25.5428Z" fill="white"/>
                                        </svg>
                                    )}

                                    {/* Delete button - between name and quantity */}
                                    <button
                                        onClick={() => handleRemoveItem(item.id)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <svg width="21" height="24" viewBox="0 0 21 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M19.9106 4.66926H14.5556V4.10895C14.5556 1.84102 12.7444 0 10.5131 0C8.28188 0 6.47062 1.84102 6.47062 4.10895V4.66926H1.08938C0.485625 4.66926 0 5.16287 0 5.77654C0 6.39022 0.485625 6.88382 1.08938 6.88382H2.625V19.9978C2.625 22.2123 4.38375 24 6.5625 24H14.4375C16.6162 24 18.375 22.2123 18.375 19.4375V6.89717H19.9106C20.5144 6.89717 21 6.40356 21 5.78988C21 5.17621 20.5144 4.6826 19.9106 4.6826V4.66926ZM8.85938 18.677C8.85938 19.4108 8.26875 20.0111 7.54688 20.0111C6.825 20.0111 6.23438 19.4108 6.23438 18.677V10.6726C6.23438 9.93886 6.825 9.33852 7.54688 9.33852C8.26875 9.33852 8.85938 9.93886 8.85938 10.6726V18.677ZM14.7656 18.677C14.7656 19.4108 14.175 20.0111 13.4531 20.0111C12.7312 20.0111 12.1406 19.4108 12.1406 18.677V10.6726C12.1406 9.93886 12.7312 9.33852 13.4531 9.33852C14.175 9.33852 14.7656 9.93886 14.7656 10.6726V18.677ZM8.63625 4.10895C8.63625 3.06837 9.47625 2.21456 10.5 2.21456C11.5238 2.21456 12.3638 3.06837 12.3638 4.10895V4.66926H8.64937V4.10895H8.63625Z" fill="#1E1E1E"/>
                                        </svg>
                                    </button>

                                    {/* Quantity - 130px width, right aligned */}
                                    <div style={{
                                        width: 130,
                                        fontSize: 28,
                                        fontWeight: 500,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: '#1E1E1E',
                                        textAlign: 'right'
                                    }}>
                                        {item.quantity} шт
                                    </div>

                                    {/* Price - 200px width, right aligned */}
                                    <div style={{
                                        width: 200,
                                        fontSize: 28,
                                        fontWeight: 500,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: '#1E1E1E',
                                        textAlign: 'right'
                                    }}>
                                        {(item.price * item.quantity).toLocaleString()} ₸
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Package button - after cart items */}
                    {packageProduct && (
                        <button
                            onClick={() => addToCart(packageProduct)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                height: '72px',
                                background: '#FFFFFF',
                                border: '1px solid #BBBBBB',
                                borderRadius: '16px',
                                padding: '0 24px',
                                cursor: 'pointer',
                                fontSize: '24px',
                                fontWeight: 500,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: '#1E1E1E',
                                marginLeft: 'auto',
                                width: 'fit-content',
                                marginTop: '24px'
                            }}>
                        <svg width="32" height="49" viewBox="0 0 32 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1.54933e-05 44V2C1.54933e-05 0.895432 0.895446 1.36049e-06 2.00002 1.36049e-06H6.00002C7.10458 1.36049e-06 8.00002 0.895432 8.00002 2V10.5006C8.00002 13.262 10.2386 15.5006 13 15.5006H17.5C20.2614 15.5006 22.5 13.262 22.5 10.5006V2C22.5 0.895432 23.3954 1.36049e-06 24.5 1.36049e-06H29.1245C30.229 1.36049e-06 31.1245 0.895431 31.1245 2V44C31.1245 46.7614 28.8859 49 26.1245 49H5.00002C2.23859 49 1.54933e-05 46.7614 1.54933e-05 44Z" fill="#A5E606"/>
                            <path d="M1.54933e-05 44V2C1.54933e-05 0.895432 0.895446 1.36049e-06 2.00002 1.36049e-06H6.00002C7.10458 1.36049e-06 8.00002 0.895432 8.00002 2V10.5006C8.00002 13.262 10.2386 15.5006 13 15.5006H17.5C20.2614 15.5006 22.5 13.262 22.5 10.5006V2C22.5 0.895432 23.3954 1.36049e-06 24.5 1.36049e-06H29.1245C30.229 1.36049e-06 31.1245 0.895431 31.1245 2V44C31.1245 46.7614 28.8859 49 26.1245 49H5.00002C2.23859 49 1.54933e-05 46.7614 1.54933e-05 44Z" fill="url(#paint0_linear_2036_2304)"/>
                            <g filter="url(#filter0_i_2036_2304)">
                                <path d="M16.2244 41.7213L16.2244 40.7017L31.1245 40.7017L31.1245 43.7603L19.8365 43.7603L20.7395 46.5924L18.053 47.3174L16.2244 41.7213Z" fill="#80B024"/>
                                <path d="M16.1931 32.6194L16.1931 31.5999L31.0932 31.5999L31.0932 34.6585L19.8053 34.6585L20.7083 37.4905L18.0218 38.2155L16.1931 32.6194Z" fill="#80B024"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M29.8602 19.2976C28.8368 18.2101 27.5725 17.6663 26.0675 17.6663C24.6677 17.6361 23.4712 18.1345 22.4779 19.1616C21.4695 20.1887 20.9803 21.3442 21.0104 22.628L21.0104 22.7187L16.2244 19.2749L16.2244 22.8773L21.3717 26.6156C22.6811 27.5671 23.697 28.1637 24.4194 28.4054C24.9462 28.5715 25.488 28.6546 26.0449 28.6546C27.5349 28.6848 28.8067 28.1486 29.8602 27.046C30.4293 26.4422 30.8403 25.7877 31.0932 25.0826L31.0984 21.3172C30.8463 20.58 30.4335 19.9068 29.8602 19.2976ZM26.0675 20.7023C26.7899 20.7023 27.3919 20.9439 27.8735 21.4273C28.3551 21.8955 28.596 22.477 28.596 23.1718C28.596 23.8666 28.3551 24.4557 27.8735 24.939C27.3769 25.4072 26.7748 25.6413 26.0675 25.6413C25.3601 25.6413 24.7656 25.4072 24.284 24.939C23.8023 24.4557 23.5615 23.8666 23.5615 23.1718C23.5615 22.477 23.8023 21.8955 24.284 21.4273C24.7505 20.9439 25.345 20.7023 26.0675 20.7023Z" fill="#80B024"/>
                            </g>
                            <defs>
                                <filter id="filter0_i_2036_2304" x="16.1931" y="17.665" width="14.9313" height="40.6523" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                    <feOffset dy="11"/>
                                    <feGaussianBlur stdDeviation="20"/>
                                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                    <feColorMatrix type="matrix" values="0 0 0 0 0.443151 0 0 0 0 0.612436 0 0 0 0 0.116672 0 0 0 1 0"/>
                                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2036_2304"/>
                                </filter>
                                <linearGradient id="paint0_linear_2036_2304" x1="21.0867" y1="13.5289" x2="-7.48123" y2="21.2233" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#9EDB09"/>
                                    <stop offset="1" stopColor="#ADF71D"/>
                                </linearGradient>
                            </defs>
                        </svg>
                            <span>{t('addPackage')}</span>
                        </button>
                    )}

                </div>

                {/* Bottom section - fixed at bottom */}
                <div style={{
                    position: 'fixed',
                    bottom: '175px', // Above footer (110px + 65px margin)
                    left: '65px',
                    right: '65px',
                    background: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '32px',
                    paddingTop: '32px'
                }}>
                    {/* Age verification warning for 21+ items */}
                    {cart.some(item => item.is_adult === 1) && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '24px',
                            background: 'rgba(30, 30, 30, 0.15)',
                            borderRadius: '50px',
                            padding: '26px 32px',
                            height: '165px'
                        }}>
                            <svg width="113" height="113" viewBox="0 0 113 113" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                                <circle cx="56.5" cy="56.5" r="56.5" fill="#1E1E1E"/>
                                <path d="M37.5838 66.4844H49.4078V71.9525L26 72V70.6211L37.3434 58.4487C38.9456 56.7053 40.0832 55.2472 40.7561 54.0743C41.461 52.8697 41.8135 51.6969 41.8135 50.5557C41.8135 49.1927 41.413 48.0832 40.6119 47.2273C39.8108 46.3715 38.7373 45.9435 37.3915 45.9435C36.0457 45.9435 34.9562 46.3873 34.1231 47.2749C33.322 48.1625 32.9214 49.3195 32.9214 50.7459C32.9214 51.2214 33.0015 51.9822 33.1617 53.0282H26.6249C26.4006 52.2358 26.2884 51.3482 26.2884 50.3655C26.2884 47.576 27.3298 45.1511 29.4127 43.0906C31.5275 41.0302 34.2192 40 37.4876 40C40.6599 40 43.2875 40.9668 45.3703 42.9005C47.4532 44.8024 48.4946 47.1798 48.4946 50.0327C48.4946 52.1565 47.9178 54.1377 46.7642 55.9762C45.5466 57.8782 43.8483 59.9544 41.6693 62.2051L37.5838 66.4844Z" fill="#DDDDDD"/>
                                <path d="M64.6438 40.5706H66.8067V71.9525H60.3179V48.1783L54.3097 50.0802L52.7716 44.422L64.6438 40.5706Z" fill="#DDDDDD"/>
                                <path d="M81.6285 67.8633V59.4473H73.3612V53.9792H81.6285V45.6107H87.8289V53.9792H96V59.4473H87.8289V67.8633H81.6285Z" fill="#DDDDDD"/>
                            </svg>
                            <div style={{
                                fontSize: 28,
                                fontWeight: 500,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: '#1E1E1E',
                                lineHeight: 1.4
                            }}>
                                {t('ageVerificationBefore')}
                            </div>
                        </div>
                    )}

                    {/* Loyalty card */}
                    {customer ? (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                <svg width="91" height="57" viewBox="0 0 91 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="0.75" y="0.75" width="88.983" height="55.5" rx="14.25" fill="url(#paint0_radial_2035_155)" stroke="url(#paint1_linear_2035_155)" strokeWidth="1.5"/>
                                    <g filter="url(#filter0_i_2035_155)">
                                        <path d="M67.046 42.6962L67.046 41.1833L89 41.1833L89 45.7218L72.3682 45.7218L73.6987 49.9242L69.7404 51L67.046 42.6962Z" fill="#80B024"/>
                                        <path d="M67 29.1903L67 27.6774L88.954 27.6774L88.954 32.216L72.3222 32.216L73.6527 36.4183L69.6944 37.4941L67 29.1903Z" fill="#80B024"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M87.1372 9.42247C85.6293 7.80877 83.7665 7.00192 81.5489 7.00192C79.4866 6.9571 77.7236 7.69671 76.26 9.22076C74.7743 10.7448 74.0535 12.4594 74.0979 14.3644L74.0979 14.4989L67.046 9.38886L67.046 14.7342L74.6301 20.2813C76.5594 21.6933 78.0563 22.5786 79.1207 22.9372C79.8969 23.1838 80.6952 23.307 81.5157 23.307C83.7111 23.3519 85.5849 22.5562 87.1372 20.9201C87.9758 20.0242 88.5814 19.053 88.954 18.0066L88.9617 12.4194C88.5901 11.3254 87.982 10.3265 87.1372 9.42247ZM81.5489 11.5068C82.6134 11.5068 83.5004 11.8654 84.21 12.5826C84.9197 13.2774 85.2745 14.1403 85.2745 15.1713C85.2745 16.2023 84.9197 17.0764 84.21 17.7936C83.4782 18.4883 82.5912 18.8357 81.5489 18.8357C80.5067 18.8357 79.6307 18.4883 78.9211 17.7936C78.2115 17.0764 77.8567 16.2023 77.8567 15.1713C77.8567 14.1403 78.2115 13.2774 78.9211 12.5826C79.6086 11.8654 80.4845 11.5068 81.5489 11.5068Z" fill="#80B024"/>
                                    </g>
                                    <defs>
                                        <filter id="filter0_i_2035_155" x="67" y="7" width="22" height="55" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                            <feOffset dy="11"/>
                                            <feGaussianBlur stdDeviation="20"/>
                                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                            <feColorMatrix type="matrix" values="0 0 0 0 0.443151 0 0 0 0 0.612436 0 0 0 0 0.116672 0 0 0 1 0"/>
                                            <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2035_155"/>
                                        </filter>
                                        <radialGradient id="paint0_radial_2035_155" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(66.2132 16.7774) rotate(149.085) scale(72.498 115.085)">
                                            <stop stopColor="#9FDF00"/>
                                            <stop offset="1" stopColor="#BDFF1A"/>
                                        </radialGradient>
                                        <linearGradient id="paint1_linear_2035_155" x1="61.3019" y1="15.7377" x2="3.8024e-06" y2="57" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#ADF71D"/>
                                            <stop offset="1" stopColor="#9EDB09"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <div>
                                    <div style={{
                                        fontSize: 28,
                                        fontWeight: 600,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: '#17181A',
                                        marginBottom: 4
                                    }}>
                                        {customer.full_name}
                                    </div>
                                    <div style={{
                                        fontSize: 24,
                                        fontWeight: 500,
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: '#739F1F'
                                    }}>
                                        {t('youHave')} {customer.balance} {t('points')}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    fontSize: 24,
                                    fontWeight: 500,
                                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                    color: '#17181A'
                                }}>
                                    {t('spendBonus')}
                                </div>
                                <Switch
                                    checked={usePoints}
                                    onChange={setUsePoints}
                                    size="default"
                                    style={{ background: usePoints ? '#739F1F' : undefined }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '24px'
                        }}>
                            <svg width="91" height="57" viewBox="0 0 91 57" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.75" y="0.75" width="88.983" height="55.5" rx="14.25" fill="url(#paint0_radial_2035_155)" stroke="url(#paint1_linear_2035_155)" strokeWidth="1.5"/>
                                <g filter="url(#filter0_i_2035_155)">
                                    <path d="M67.046 42.6962L67.046 41.1833L89 41.1833L89 45.7218L72.3682 45.7218L73.6987 49.9242L69.7404 51L67.046 42.6962Z" fill="#80B024"/>
                                    <path d="M67 29.1903L67 27.6774L88.954 27.6774L88.954 32.216L72.3222 32.216L73.6527 36.4183L69.6944 37.4941L67 29.1903Z" fill="#80B024"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M87.1372 9.42247C85.6293 7.80877 83.7665 7.00192 81.5489 7.00192C79.4866 6.9571 77.7236 7.69671 76.26 9.22076C74.7743 10.7448 74.0535 12.4594 74.0979 14.3644L74.0979 14.4989L67.046 9.38886L67.046 14.7342L74.6301 20.2813C76.5594 21.6933 78.0563 22.5786 79.1207 22.9372C79.8969 23.1838 80.6952 23.307 81.5157 23.307C83.7111 23.3519 85.5849 22.5562 87.1372 20.9201C87.9758 20.0242 88.5814 19.053 88.954 18.0066L88.9617 12.4194C88.5901 11.3254 87.982 10.3265 87.1372 9.42247ZM81.5489 11.5068C82.6134 11.5068 83.5004 11.8654 84.21 12.5826C84.9197 13.2774 85.2745 14.1403 85.2745 15.1713C85.2745 16.2023 84.9197 17.0764 84.21 17.7936C83.4782 18.4883 82.5912 18.8357 81.5489 18.8357C80.5067 18.8357 79.6307 18.4883 78.9211 17.7936C78.2115 17.0764 77.8567 16.2023 77.8567 15.1713C77.8567 14.1403 78.2115 13.2774 78.9211 12.5826C79.6086 11.8654 80.4845 11.5068 81.5489 11.5068Z" fill="#80B024"/>
                                </g>
                                <defs>
                                    <filter id="filter0_i_2035_155" x="67" y="7" width="22" height="55" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                        <feOffset dy="11"/>
                                        <feGaussianBlur stdDeviation="20"/>
                                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                                        <feColorMatrix type="matrix" values="0 0 0 0 0.443151 0 0 0 0 0.612436 0 0 0 0 0.116672 0 0 0 1 0"/>
                                        <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2035_155"/>
                                    </filter>
                                    <radialGradient id="paint0_radial_2035_155" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(66.2132 16.7774) rotate(149.085) scale(72.498 115.085)">
                                        <stop stopColor="#9FDF00"/>
                                        <stop offset="1" stopColor="#BDFF1A"/>
                                    </radialGradient>
                                    <linearGradient id="paint1_linear_2035_155" x1="61.3019" y1="15.7377" x2="3.8024e-06" y2="57" gradientUnits="userSpaceOnUse">
                                        <stop stopColor="#ADF71D"/>
                                        <stop offset="1" stopColor="#9EDB09"/>
                                    </linearGradient>
                                </defs>
                            </svg>
                            <div style={{
                                fontSize: 28,
                                fontWeight: 500,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: '#739F1F'
                            }}>
                                {t('scanLoyaltyCard')}
                            </div>
                        </div>
                    )}

                    {/* Total */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            fontSize: 60,
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#17181A'
                        }}>
                            {t('total')}
                        </div>
                        <div style={{
                            fontSize: 60,
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#17181A'
                        }}>
                            {getFinalAmount().toLocaleString()} ₸
                        </div>
                    </div>

                    {/* Proceed button */}
                    <button
                        onClick={handleProceedToPayment}
                        disabled={cart.length === 0}
                        style={{
                            width: '100%',
                            height: '124px',
                            background: '#028A62',
                            border: 'none',
                            borderRadius: '50px',
                            fontSize: '38px',
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: 'white',
                            cursor: cart.length === 0 ? 'not-allowed' : 'pointer',
                            opacity: cart.length === 0 ? 0.5 : 1,
                            transition: 'all 0.3s'
                        }}
                    >
                        {t('proceedToPayment')}
                    </button>
                </div>
            </div>

            {/* Age Verification Modal */}
            {ageVerificationModalVisible && (
                <AgeVerificationModal
                    onVerified={handleAgeVerified}
                    onClose={() => {
                        setAgeVerificationModalVisible(false);
                    }}
                />
            )}

            {/* Marking Code Modal */}
            {markingModalVisible && (
                <MarkingCodeModal
                    product={pendingMarkingProduct}
                    onScanned={handleMarkingScanned}
                    onClose={() => {
                        setMarkingModalVisible(false);
                        setPendingMarkingProduct(null);
                    }}
                />
            )}

            {/* Inactivity Warning Modal */}
            {inactivityModalVisible && (
                <div className="self-service-modal-overlay">
                    <div className="self-service-modal" style={{ maxWidth: '800px' }}>
                        <h2 style={{
                            fontSize: 50,
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#17181A',
                            marginBottom: 32,
                            lineHeight: 1.3
                        }}>
                            Вы здесь?
                        </h2>

                        <p style={{
                            fontSize: 35,
                            fontWeight: 500,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#666',
                            marginBottom: 32,
                            lineHeight: 1.6
                        }}>
                            Корзина будет очищена через {countdown} секунд
                        </p>

                        <button
                            onClick={() => setInactivityModalVisible(false)}
                            style={{
                                width: '100%',
                                height: '124px',
                                background: '#028A62',
                                border: 'none',
                                borderRadius: '50px',
                                fontSize: '38px',
                                fontWeight: 700,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.3s'
                            }}
                        >
                            Я здесь
                        </button>
                    </div>
                </div>
            )}

            {/* Product Not Found Modal */}
            {productNotFoundModalVisible && (
                <div className="self-service-modal-overlay">
                    <div className="self-service-modal" style={{ maxWidth: '800px' }}>
                        <h2 style={{
                            fontSize: 50,
                            fontWeight: 700,
                            fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                            color: '#17181A',
                            marginBottom: 48,
                            lineHeight: 1.3,
                            textAlign: 'center'
                        }}>
                            Товар не найден
                        </h2>

                        <button
                            onClick={() => setProductNotFoundModalVisible(false)}
                            style={{
                                width: '100%',
                                height: '124px',
                                background: '#028A62',
                                border: 'none',
                                borderRadius: '50px',
                                fontSize: '38px',
                                fontWeight: 700,
                                fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                color: 'white',
                                cursor: 'pointer',
                                transition: 'all 0.3s'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Quick Products Modal */}
            {quickProductsModalVisible && selectedCategory && (
                <div
                    className="self-service-modal-overlay"
                    style={{ backdropFilter: 'blur(10px)' }}
                    onClick={() => setQuickProductsModalVisible(false)}
                >
                    <div
                        className="self-service-modal"
                        style={{
                            maxWidth: '800px',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="self-service-modal__title" style={{ marginBottom: 24 }}>
                            {selectedCategory}
                        </h2>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '16px'
                        }}>
                            {quickProducts.filter(p => p.category === selectedCategory).map(product => (
                                <button
                                    key={product.id}
                                    onClick={() => {
                                        addToCart(product);
                                        setQuickProductsModalVisible(false);
                                    }}
                                    style={{
                                        padding: '16px',
                                        background: '#F5F5F5',
                                        border: '1px solid #E0E0E0',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                        color: '#17181A',
                                        textAlign: 'left',
                                        transition: 'all 0.2s',
                                        minHeight: '100px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = '#E8E8E8';
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = '#F5F5F5';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>
                                        {product.displayName || product.name}
                                    </div>
                                    <div style={{ fontSize: '16px', color: '#666' }}>
                                        {product._needsFullData ? 'Нажмите для добавления' : `${product.price} ₸`}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Footer with quick products buttons */}
            <SelfServiceFooter
                quickProductsButtons={
                    categories.length > 0 ? categories.map(category => {
                        console.log('Rendering button for category:', category);
                        return (
                            <button
                                key={category}
                                onClick={() => {
                                    setSelectedCategory(category);
                                    setQuickProductsModalVisible(true);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#3B3B3B',
                                    border: 'none',
                                    borderRadius: '21px',
                                    padding: '0 20px',
                                    height: '72px',
                                    fontSize: '24px',
                                    fontWeight: 500,
                                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#4B4B4B'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#3B3B3B'}
                            >
                                {category}
                            </button>
                        );
                    }) : null
                }
            />
        </div>
        </>
    );
};

// Age Verification Modal Component
const AgeVerificationModal = ({ onVerified, onClose }) => {
    const { t } = useSelfServiceLanguage();
    const [barcodeBuffer, setBarcodeBuffer] = useState('');
    const [lastKeystroke, setLastKeystroke] = useState(0);

    // Валидация ИИН
    const validateIIN = (iin) => {
        if (iin.length !== 12 || !/^\d{12}$/.test(iin)) {
            return { valid: false, error: 'ИИН должен содержать 12 цифр' };
        }

        // Извлекаем дату рождения
        const yy = parseInt(iin.substring(0, 2), 10);
        const mm = parseInt(iin.substring(2, 4), 10);
        const dd = parseInt(iin.substring(4, 6), 10);
        const century = parseInt(iin.charAt(6), 10);

        // Определяем век
        let year;
        if (century === 1 || century === 2) {
            year = 1800 + yy;
        } else if (century === 3 || century === 4) {
            year = 1900 + yy;
        } else if (century === 5 || century === 6) {
            year = 2000 + yy;
        } else {
            return { valid: false, error: 'Неверный формат ИИН (неверный признак века)' };
        }

        // Проверка корректности даты
        if (mm < 1 || mm > 12 || dd < 1 || dd > 31) {
            return { valid: false, error: 'Неверная дата рождения в ИИН' };
        }

        // Создаем дату
        const birthDateObj = new Date(year, mm - 1, dd);

        // Проверяем, что дата корректна
        if (
            birthDateObj.getFullYear() !== year ||
            birthDateObj.getMonth() !== mm - 1 ||
            birthDateObj.getDate() !== dd
        ) {
            return { valid: false, error: 'Некорректная дата рождения в ИИН' };
        }

        return { valid: true, birthDate: birthDateObj };
    };

    // Проверка возраста (полные годы)
    const checkAge = (birthDateObj, requiredAge) => {
        const today = new Date();
        let age = today.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = today.getMonth() - birthDateObj.getMonth();
        const dayDiff = today.getDate() - birthDateObj.getDate();

        // Если день рождения еще не наступил в этом году
        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
            age--;
        }

        return age >= requiredAge;
    };

    // Проверка кода
    const handleBarcodeVerify = (code) => {
        // Check if it's cashier UUID
        try {
            const cashierInfo = localStorage.getItem('device_cashier_info');
            if (cashierInfo) {
                const cashierData = JSON.parse(cashierInfo);
                if (cashierData?.cashier?.uuid && cashierData.cashier.uuid === code) {
                    // Cashier verified
                    onVerified();
                    return;
                }
            }
        } catch (err) {
            console.error('Error checking cashier UUID:', err);
        }

        // Check if it's a valid IIN
        const validation = validateIIN(code);
        if (!validation.valid) {
            const audio = new Audio('/sounds/error.m4a');
            audio.play().catch(err => console.error('Error playing sound:', err));
            return;
        }

        // Проверяем возраст (21 год)
        if (!checkAge(validation.birthDate, 21)) {
            const audio = new Audio('/sounds/error.m4a');
            audio.play().catch(err => console.error('Error playing sound:', err));
            return;
        }

        // Age verified successfully
        onVerified();
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            const currentTime = Date.now();
            const timeDiff = currentTime - lastKeystroke;

            if (timeDiff > 200) {
                setBarcodeBuffer('');
            }
            setLastKeystroke(currentTime);

            if (e.key === 'Enter') {
                if (barcodeBuffer.length >= 4) {
                    handleBarcodeVerify(barcodeBuffer);
                }
                setBarcodeBuffer('');
                e.preventDefault();
            } else if (e.key.length === 1) {
                setBarcodeBuffer(prev => prev + e.key);
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [barcodeBuffer, lastKeystroke, onVerified]);

    return (
        <div className="self-service-modal-overlay">
            <div className="self-service-modal" style={{ maxWidth: '1000px' }}>
                <h2 style={{
                    fontSize: 50,
                    fontWeight: 700,
                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                    color: '#17181A',
                    marginBottom: 32,
                    lineHeight: 1.3,
                    whiteSpace: 'pre-line'
                }}>
                    {t('ageVerificationTitle')}
                </h2>

                <div className="self-service-scanner">
                    <div className="self-service-scanner__frame">
                        <div className="self-service-scanner__line" />
                        <svg width="100" height="100" viewBox="0 0 100 100" style={{ margin: 'auto' }}>
                            <rect x="10" y="30" width="80" height="40" fill="none" stroke="#17181A" strokeWidth="3"/>
                            <line x1="20" y1="40" x2="20" y2="60" stroke="#17181A" strokeWidth="2"/>
                            <line x1="25" y1="40" x2="25" y2="60" stroke="#17181A" strokeWidth="1"/>
                            <line x1="28" y1="40" x2="28" y2="60" stroke="#17181A" strokeWidth="3"/>
                            <line x1="32" y1="40" x2="32" y2="60" stroke="#17181A" strokeWidth="2"/>
                            <line x1="36" y1="40" x2="36" y2="60" stroke="#17181A" strokeWidth="1"/>
                            <line x1="40" y1="40" x2="40" y2="60" stroke="#17181A" strokeWidth="2"/>
                            <line x1="45" y1="40" x2="45" y2="60" stroke="#17181A" strokeWidth="3"/>
                            <line x1="50" y1="40" x2="50" y2="60" stroke="#17181A" strokeWidth="1"/>
                            <line x1="55" y1="40" x2="55" y2="60" stroke="#17181A" strokeWidth="2"/>
                            <line x1="60" y1="40" x2="60" y2="60" stroke="#17181A" strokeWidth="3"/>
                            <line x1="65" y1="40" x2="65" y2="60" stroke="#17181A" strokeWidth="1"/>
                            <line x1="70" y1="40" x2="70" y2="60" stroke="#17181A" strokeWidth="2"/>
                            <line x1="75" y1="40" x2="75" y2="60" stroke="#17181A" strokeWidth="3"/>
                            <line x1="80" y1="40" x2="80" y2="60" stroke="#17181A" strokeWidth="1"/>
                        </svg>
                    </div>
                </div>

                <p style={{
                    fontSize: 35,
                    fontWeight: 500,
                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                    color: '#666',
                    marginBottom: 32,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line'
                }}>
                    {t('ageVerificationText')}
                </p>

                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#028A62',
                        fontSize: 35,
                        fontWeight: 500,
                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                        cursor: 'pointer',
                        padding: 0,
                        marginTop: 16
                    }}
                >
                    Назад
                </button>
            </div>
        </div>
    );
};

// Marking Code Modal Component
const MarkingCodeModal = ({ product, onScanned, onClose }) => {
    const { t } = useSelfServiceLanguage();
    const [barcodeBuffer, setBarcodeBuffer] = useState('');
    const [lastKeystroke, setLastKeystroke] = useState(0);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const currentTime = Date.now();
            const timeDiff = currentTime - lastKeystroke;

            if (timeDiff > 200) {
                setBarcodeBuffer('');
            }
            setLastKeystroke(currentTime);

            if (e.key === 'Enter') {
                if (barcodeBuffer.length >= 29 && isMarkingCode(barcodeBuffer)) {
                    const parsed = parseMarkingCode(barcodeBuffer);
                    if (parsed) {
                        onScanned(parsed.markCode);
                    }
                }
                setBarcodeBuffer('');
                e.preventDefault();
            } else if (e.key.length === 1) {
                setBarcodeBuffer(prev => prev + e.key);
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [barcodeBuffer, lastKeystroke, onScanned]);

    return (
        <div className="self-service-modal-overlay">
            <div className="self-service-modal" style={{ maxWidth: '1000px' }}>
                <h2 style={{
                    fontSize: 50,
                    fontWeight: 700,
                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                    color: '#17181A',
                    marginBottom: 32,
                    lineHeight: 1.3,
                    whiteSpace: 'pre-line'
                }}>
                    {t('markingCodeTitle')}
                </h2>

                <div className="self-service-scanner">
                    <div className="self-service-scanner__frame">
                        <div className="self-service-scanner__line" />
                        <svg width="127" height="120" viewBox="0 0 454 345" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M57.4447 0.374752L113 0V16.8739L65.8893 17.2487V61.3476H49V8.81168C49 4.16783 52.7966 0.374752 57.4447 0.374752ZM65.8893 283.402V327.501H112.5V344.375H57.4447C52.7966 344.375 49 340.617 49 335.938V283.402H65.8893Z"
                                fill="#5C5B5B"/>
                            <path
                                d="M396.555 0.749344L341 0.375V17.2305L388.111 17.6049V61.6558H405V9.17709C405 4.5383 401.203 0.749344 396.555 0.749344ZM388.111 283.468V327.519H341.5V344.375H396.555C401.203 344.375 405 340.621 405 335.947V283.468H388.111Z"
                                fill="#5C5B5B"/>
                            <path
                                d="M113 263.575H341V286.375H113V263.575ZM272.6 240.775H318.2V263.575H272.6V240.775ZM113 240.775H135.8V263.575H113V240.775ZM295.4 217.975H341V240.775H295.4V217.975ZM249.8 217.975H272.6V240.775H249.8V217.975ZM181.4 217.975H204.2V240.775H181.4V217.975ZM113 217.975H135.8V240.775H113V217.975ZM249.8 195.175H295.4V217.975H249.8V195.175ZM113 195.175H227V217.975H113V195.175ZM318.2 172.375H341V195.175H318.2V172.375ZM272.6 172.375H295.4V195.175H272.6V172.375ZM227 172.375H249.8V195.175H227V172.375ZM113 172.375H158.6V195.175H113V172.375ZM113 149.575H249.8V172.375H113V149.575ZM318.2 126.775H341V149.575H318.2V126.775ZM227 126.775H295.4V149.575H227V126.775ZM113 126.775H204.2V149.575H113V126.775ZM272.6 103.975H295.4V126.775H272.6V103.975ZM113 103.975H181.4V126.775H113V103.975ZM227 81.175H341V103.975H227V81.175ZM113 81.175H135.8V103.975H113V81.175ZM295.4 58.375H318.2V81.175H295.4V58.375ZM249.8 58.375H272.6V81.175H249.8V58.375ZM204.2 58.375H227V81.175H204.2V58.375ZM158.6 58.375H181.4V81.175H158.6V58.375ZM113 58.375H135.8V81.175H113V58.375Z"
                                fill="#5C5B5B"/>
                            <path d="M0 175.375H454" stroke="#EB0000" strokeOpacity="0.5" strokeWidth="21"/>
                        </svg>
                    </div>
                </div>

                <p style={{
                    fontSize: 35,
                    fontWeight: 500,
                    fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                    color: '#666',
                    marginBottom: 32,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line'
                }}>
                    {t('markingCodeText')}
                </p>

                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: '#028A62',
                        fontSize: 35,
                        fontWeight: 500,
                        fontFamily: 'Cera Pro, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
                        cursor: 'pointer',
                        padding: 0,
                        marginTop: 16
                    }}
                >
                    Назад
                </button>
            </div>
        </div>
    );
};

export default SelfServiceShopScreen;
